import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ThreeCircles } from 'react-loader-spinner';
import { Fetching } from '../UI/Modal/Modal';
import SchoolAgreementModal from './SchoolAgreementModal';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LoginIcon from '@mui/icons-material/Login';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ForumIcon from '@mui/icons-material/Forum';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import BadgeIcon from '@mui/icons-material/Badge';
import InsightsIcon from '@mui/icons-material/Insights';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ShieldIcon from '@mui/icons-material/Shield';
import SpeedIcon from '@mui/icons-material/Speed';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SchoolPasswordInput from './SchoolPasswordInput';
import css from './SchoolLogin.module.css';

const TABS = [
  { key: 'student', label: 'Student Login' },
  { key: 'admin',   label: 'Admin Login'   },
  { key: 'teacher', label: 'Teacher Login' },
  { key: 'parent',  label: 'Parent Login'  },
];

const DASHBOARD_ROUTES = {
  admin: '/school/admin/dashboard',
  teacher: '/school/teacher/dashboard',
  student: '/school/student/dashboard',
  parent: '/school/parent/dashboard',
};

// ─── Left Panel Role-specific Top Benefits & Testimonials ──────────────────────
const ROLE_PANEL_CONTENT = {
  admin: {
    heading: 'The Smart School\nExam Platform',
    sub: 'Autonomous school governance, automated fee reconciliation & deep academic analytics.',
    benefits: [
      { Icon: AccountBalanceIcon, text: 'Zero-leakage automated fees & instant direct payouts' },
      { Icon: AssignmentIcon,     text: 'Full CBT exam engine with sub-second auto grading' },
      { Icon: BarChartIcon,       text: 'Executive analytics & branded printable report cards' },
      { Icon: SecurityIcon,       text: 'Role-based sub-admin delegation & password-protected audit logs' },
    ],
    testimonial: {
      quote: '"Bubble Ai Schools cut our exam management time in half and stopped fee leakage completely."',
      author: 'Mrs. Okafor, Vice Principal — Lagos',
    },
  },
  teacher: {
    heading: 'AI-Supercharged\nTeaching Suite',
    sub: 'Generative exam creation, automatic grading, lesson note synthesis & rich diagnostics.',
    benefits: [
      { Icon: AutoFixHighIcon,    text: 'Generative AI question bank tailored to NERDC curriculum' },
      { Icon: CheckCircleIcon,    text: 'Zero grading lag with automated instant question diagnostics' },
      { Icon: AutoStoriesIcon,    text: 'Curriculum-aligned scheme-of-work AI lesson plans' },
      { Icon: EventAvailableIcon, text: 'Subject-by-subject per-period digital attendance tracker' },
    ],
    testimonial: {
      quote: '"Creating mock exams and grading 150 students used to take days. Now it takes minutes."',
      author: 'Mr. Adeyemi, Senior Physics & Maths Teacher',
    },
  },
  student: {
    heading: 'Your AI Academic\n& CBT Copilot',
    sub: 'Personalized 24/7 AI tutoring, distraction-free CBT simulator & career path forecasting.',
    benefits: [
      { Icon: AssignmentIcon,  text: 'Distraction-free CBT exam simulator with automatic answer saving' },
      { Icon: SmartToyIcon,    text: '24/7 personal AI tutor for step-by-step homework & study help' },
      { Icon: AutoAwesomeIcon, text: 'Predictive career path guidance calibrated to your subject strengths' },
      { Icon: InsightsIcon,    text: 'Instant score breakdowns and topic mastery analytics after every test' },
    ],
    testimonial: {
      quote: '"The AI explanations showed me exactly where I went wrong. My CA test scores jumped from 60% to 88%."',
      author: 'Chinedu O., SS3 Science Student',
    },
  },
  parent: {
    heading: '360° Child Progress\n& Fee Portal',
    sub: 'Cashless fee settlement, real-time grade alerts & direct communication with teachers.',
    benefits: [
      { Icon: PaymentsIcon,       text: '1-click secure fee payments via cards, transfer, or USSD' },
      { Icon: FamilyRestroomIcon, text: 'Unified family dashboard to monitor all children in one place' },
      { Icon: BarChartIcon,       text: 'Instant access to approved exam results and term report cards' },
      { Icon: EventAvailableIcon, text: 'Real-time period attendance notifications and absence alerts' },
    ],
    testimonial: {
      quote: '"I can pay school fees from my phone in 30 seconds and track my kids and their performance at school every single day."',
      author: 'Mrs. Balogun, Parent of 3 — Abuja',
    },
  },
};

// ─── Stakeholder Platform Benefits Matrix ─────────────────────────────────────
const STAKEHOLDER_BENEFITS = {
  admin: {
    role: 'School Administrators & Principals',
    tagline: 'Autonomous School Governance & Institutional Excellence',
    description: 'A unified operating system engineered to eliminate administrative bottlenecks, protect school revenues, streamline compliance, and elevate institutional academic standing.',
    highlights: [
      {
        Icon: AccountBalanceIcon,
        title: 'Zero-Leakage Automated Fees & Instant Payouts',
        desc: 'Direct-to-bank collection via Flutterwave with automated multi-channel invoice dispatch, fee structures, and transparent ledger reconciliation.',
        pill: 'FinTech Grade',
      },
      {
        Icon: AssignmentIcon,
        title: 'Full-Scale CBT & Term Exam Engine',
        desc: 'Deliver secure, timed continuous assessments (CA), mock tests, and term exams with instant grading (THEORY and MCQs) and multi-class distribution.',
        pill: 'Sub-second Grading',
      },
      {
        Icon: BarChartIcon,
        title: 'Executive Analytics & Printable Report Cards',
        desc: 'Generate branded PDF/CSV broadsheets, teacher grade distributions, subject benchmarks, and early student intervention alerts.',
        pill: 'Real-time BI',
      },
      {
        Icon: BadgeIcon,
        title: 'All-Staff Payroll & Expense Accounting',
        desc: 'Run teaching and non-teaching monthly payrolls with tax deductions, allowances, and live operational expense tracking.',
        pill: 'HR & Audit Ready',
      },
      {
        Icon: Inventory2Icon,
        title: 'Store, Inventory & Asset Lifecycle',
        desc: 'Monitor school assets, textbooks, uniforms, and lab supplies with restock threshold alerts and requisition logs.',
        pill: 'Asset Shield',
      },
      {
        Icon: SecurityIcon,
        title: 'Role-Based Sub-Admin Delegation',
        desc: 'Delegate granular permissions to vice principals, bursars, and exam officers with password-protected audit logs.',
        pill: 'Zero Trust',
      },
    ],
    stats: [
      { value: '100%', label: 'Exam Integrity & Real-time Auto-save' },
      { value: '10x', label: 'Faster Term Result Publishing' },
      { value: '0%', label: 'Fee Leakage with Direct Reconciliation' },
    ],
  },
  teacher: {
    role: 'Educators & Subject Specialists',
    tagline: 'AI-Supercharged Teaching, Effortless Exam Crafting & Insightful Mentorship',
    description: 'Reclaim your classroom hours with generative AI exam creation, automatic grading, lesson note synthesis, and rich student diagnostic profiles.',
    highlights: [
      {
        Icon: AutoFixHighIcon,
        title: 'Generative AI Question Bank & Exam Builder',
        desc: 'Draft comprehensive multiple-choice and theoretical exams in seconds tailored to Nigerian national curriculum and Bloom’s taxonomy.',
        pill: 'AI Co-pilot',
      },
      {
        Icon: CheckCircleIcon,
        title: 'Automated Marking & Instant Diagnostics',
        desc: 'Eliminate manual paper grading (whether THEORY or MCQs). Instantly spot high-error questions, student misconceptions, and class mastery rates.',
        pill: 'Zero Grading Lag',
      },
      {
        Icon: AutoStoriesIcon,
        title: 'Curriculum-Aligned AI Lesson Plans',
        desc: 'Produce weekly scheme-of-work lesson plans with clear learning objectives, materials, and automated administrative approval workflows.',
        pill: 'NERDC Aligned',
      },
      {
        Icon: EventAvailableIcon,
        title: 'Per-Period Digital Attendance Tracker',
        desc: 'Record subject-by-subject attendance in seconds from mobile or laptop, syncing instantly with school admin and parent feeds.',
        pill: 'Live Sync',
      },
      {
        Icon: ForumIcon,
        title: 'Encrypted Multi-Stakeholder Messaging',
        desc: 'Communicate directly and professionally with parents, class students, and fellow faculty without sharing personal phone numbers.',
        pill: 'Broadcast & DM',
      },
      {
        Icon: MenuBookIcon,
        title: 'Special Math, Science & Diagram Symbols',
        desc: 'Insert complex equations, scientific notations, and diagrams effortlessly into your digital question papers.',
        pill: 'LaTeX & Unicode',
      },
    ],
    stats: [
      { value: '75%', label: 'Reduction in Exam Prep & Grading Time' },
      { value: '1-Click', label: 'Curriculum Scheme Generation' },
      { value: '360°', label: 'Student Performance Visibility' },
    ],
  },
  student: {
    role: 'Students & Future Leaders',
    tagline: 'Your AI Academic Copilot, CBT Portal & Personal Career Pathway',
    description: 'Transform how you learn, take exams, and unlock your future with personalized AI tutoring, career trajectory modeling, and instant performance feedback.',
    highlights: [
      {
        Icon: AssignmentIcon,
        title: 'Interactive CBT Exam & Mock Simulator',
        desc: 'Take timed school exams, practice quizzes, and external mock simulations in a clean, distraction-free environment with auto-save.',
        pill: 'Auto-Saved Answers',
      },
      {
        Icon: SmartToyIcon,
        title: 'Personal 24/7 AI Tutor & Study Guides',
        desc: 'Get step-by-step explanations, interactive summaries, and audiovisual study plans tailored specifically to your weak areas.',
        pill: 'Always-on AI',
      },
      {
        Icon: AutoAwesomeIcon,
        title: 'Predictive AI Career Path Forecasting',
        desc: 'Discover university degrees, vocational careers, and industry skill pathways calibrated to your unique subject strengths.',
        pill: 'Next-Gen Guidance',
      },
      {
        Icon: InsightsIcon,
        title: 'Instant Exam Scores & Topic Mastery Breakdown',
        desc: 'Review question-by-question performance immediately after submission with comprehensive explanations and study pointers.',
        pill: 'Instant Clarity',
      },
      {
        Icon: ScheduleIcon,
        title: 'Live School Timetable & Master Calendar',
        desc: 'Never miss a class, CA test, sports day, or school deadline with real-time timetable updates delivered right to your pocket.',
        pill: 'Synced Schedule',
      },
      {
        Icon: ForumIcon,
        title: 'Direct Subject Teacher Q&A Desk',
        desc: 'Reach out to your subject teachers for homework guidance, exam clarification, and school announcements.',
        pill: 'Always Connected',
      },
    ],
    stats: [
      { value: '3.4x', label: 'Average Retention with AI Study Plans' },
      { value: '<1s', label: 'Instant Score Calculation' },
      { value: '100+', label: 'Curated Career Trajectories' },
    ],
  },
  parent: {
    role: 'Parents & Guardians',
    tagline: 'Unprecedented Transparency, Effortless Payments & 360° Child Monitoring',
    description: 'Stay actively connected to your children’s educational journey with real-time grade alerts, cashless school fee payments, and direct teacher communication.',
    highlights: [
      {
        Icon: PaymentsIcon,
        title: '1-Click Secure School Fee Settlement',
        desc: 'Pay tuition, bus, uniform, and exam levies seamlessly with cards, bank transfers, or USSD with instant downloadable PDF receipts.',
        pill: 'Instant Receipting',
      },
      {
        Icon: FamilyRestroomIcon,
        title: 'Multi-Child Unified Family Dashboard',
        desc: 'Seamlessly switch between all your enrolled children across different classes from a single secure parent account.',
        pill: 'Single Portal',
      },
      {
        Icon: BarChartIcon,
        title: 'Real-Time Exam Results & Report Cards',
        desc: 'View continuous assessment scores, term broadsheets, subject rankings, and teacher remarks the moment they are approved.',
        pill: 'Immediate Access',
      },
      {
        Icon: EventAvailableIcon,
        title: 'Period-by-Period Attendance & Absence Alerts',
        desc: 'Know exactly when your child is in class with real-time attendance logs and immediate notifications for missed periods.',
        pill: 'Peace of Mind',
      },
      {
        Icon: CalendarMonthIcon,
        title: 'School Master Calendar & Event Planner',
        desc: 'Stay informed on visiting days, mid-term breaks, PTA meetings, exam dates, and term milestones with calendar sync.',
        pill: 'Stay Ahead',
      },
      {
        Icon: ForumIcon,
        title: 'Direct Teacher & Administration Messenger',
        desc: 'Message class teachers and school administrators securely to discuss child welfare, academic support, and special requests.',
        pill: 'Direct Line',
      },
    ],
    stats: [
      { value: '100%', label: 'Cashless Fee Transparency' },
      { value: 'Live', label: 'Class Attendance & Result Feeds' },
      { value: '24/7', label: 'Instant PDF Invoice & Receipt Archive' },
    ],
  },
};

const TRUST_METRICS = [
  {
    Icon: ShieldIcon,
    title: 'Enterprise-Grade Security',
    subtitle: 'AES-256 Data Encryption & JWT Protected Sessions',
  },
  {
    Icon: VerifiedUserIcon,
    title: 'Bank-Grade FinTech Trust',
    subtitle: 'Secured via PCI-DSS Compliant Flutterwave Gateways',
  },
  {
    Icon: SpeedIcon,
    title: '99.98% High Availability',
    subtitle: 'Ultra-low Latency CBT & Scalable Cloud Infrastructure',
  },
  {
    Icon: MilitaryTechIcon,
    title: 'Institutional Grade Quality',
    subtitle: 'Trusted by Premier Primary, Secondary & Tertiary Institutions',
  },
];

// ─── Sub-Component: Stakeholder Benefits Showcase ─────────────────────────────
function StakeholderBenefitsShowcase({ role, onTabChange, onLoginClick, onGetStartedClick }) {
  const data = STAKEHOLDER_BENEFITS[role] || STAKEHOLDER_BENEFITS.admin;

  return (
    <section className={css.benefitsSection} aria-label={`${data.role} Benefits`}>
      {/* Ambient background glow & grid lines */}
      <div className={css.benefitsGlow} />

      {/* Strategic Brand Header Banner */}
      <div className={css.brandHeader}>
        <div className={css.brandBadge}>
          <img src="/favicon.ico" alt="Bubble Ai Schools" className={css.brandLogo} />
          <div className={css.brandTextCol}>
            <div className={css.brandRow}>
              <span className={css.brandTitle}>Bubble Ai Schools</span>
              <span className={css.brandTag}>Next-Gen Platform</span>
            </div>
            <span className={css.brandSub}>AI-Powered Academic Ecosystem & School Management OS</span>
          </div>
        </div>
      </div>

      {/* Synchronized Stakeholder Tab Switcher at the top */}
      <div className={css.benefitsTabNavWrap}>
        <div className={css.benefitsTabNav}>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`${css.benefitsTabBtn} ${role === key ? css.benefitsTabBtnActive : ''}`}
              onClick={() => onTabChange && onTabChange(key)}
            >
              <span className={css.benefitsTabDot} />
              {label.replace(' Login', '')} Portal
            </button>
          ))}
        </div>
      </div>

      {/* Animated Stakeholder Content Container with Futuristic Key-Transition */}
      <div key={role} className={css.benefitsContentAnimated}>
        {/* Futuristic Scanline Laser Sweep on Switch */}
        <div className={css.cyberScanBeam} />

        <div className={css.benefitsHeader}>
          <div className={css.benefitsPill}>
            <AutoAwesomeIcon className={css.benefitsPillIcon} />
            <span>Tailored For {data.role}</span>
          </div>
          <h3 className={css.benefitsTitle}>{data.tagline}</h3>
          <p className={css.benefitsSub}>{data.description}</p>
        </div>

        {/* Quick Action Bar (Login & Get Started) just before the cards */}
        <div className={css.benefitsCtaRow}>
          <button
            type="button"
            className={css.benefitsLoginCta}
            onClick={onLoginClick}
          >
            <LoginIcon style={{ fontSize: '1.15rem' }} />
            <span>Sign In to Your Portal</span>
          </button>
          <button
            type="button"
            className={css.benefitsGetStartedCta}
            onClick={onGetStartedClick}
          >
            <AutoAwesomeIcon style={{ fontSize: '1.05rem' }} />
            <span>Get Started with Bubble Ai</span>
          </button>
        </div>

        {/* Futuristic Grid Cards with Staggered Cascading Animation */}
        <div className={css.benefitsGrid}>
          {data.highlights.map((item, idx) => {
            const IconComponent = item.Icon;
            return (
              <div
                key={idx}
                className={css.benefitCard}
                style={{ animationDelay: `${idx * 0.055}s` }}
              >
                <div className={css.benefitCardHeader}>
                  <div className={css.benefitIconWrap}>
                    <IconComponent className={css.benefitIcon} />
                  </div>
                  {item.pill && <span className={css.benefitPill}>{item.pill}</span>}
                </div>
                <h4 className={css.benefitCardTitle}>{item.title}</h4>
                <p className={css.benefitCardDesc}>{item.desc}</p>
                <div className={css.benefitCardGlowLine} />
              </div>
            );
          })}
        </div>

        {/* KPI Stats Strip */}
        <div className={css.benefitsStatsStrip}>
          {data.stats.map((stat, i) => (
            <div key={i} className={css.benefitStatItem}>
              <span className={css.benefitStatVal}>{stat.value}</span>
              <span className={css.benefitStatLbl}>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Centered Trust, Security & Quality Bar */}
      <div className={css.trustBar}>
        <div className={css.trustBarGlow} />
        <div className={css.trustBarHeader}>
          <span className={css.trustBarRule} />
          <span className={css.trustBarTitle}>
            <ShieldIcon className={css.trustBarTitleIcon} />
            SECURITY, TRUST & INSTITUTIONAL QUALITY
          </span>
          <span className={css.trustBarRule} />
        </div>
        <div className={css.trustMetricsGrid}>
          {TRUST_METRICS.map((tm, idx) => {
            const TIcon = tm.Icon;
            return (
              <div key={idx} className={css.trustMetric}>
                <div className={css.trustMetricIconBox}>
                  <TIcon className={css.trustMetricIcon} />
                </div>
                <div className={css.trustMetricText}>
                  <div className={css.trustMetricTitle}>{tm.title}</div>
                  <div className={css.trustMetricSub}>{tm.subtitle}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  return { ok: res.ok, status: res.status, data };
}

// ─── Sub-forms ───────────────────────────────────────────────────────────────

function StudentForm({ onSuccess, onForgotPassword, onOpenAgreement }) {
  const [mode, setMode] = useState('email'); // 'email' | 'code'
  const [fields, setFields] = useState({ email: '', schoolCode: '', studentId: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [schoolCodeRequired, setSchoolCodeRequired] = useState(false);
  const [passwordChangedNotice] = useState(() => {
    const notice = sessionStorage.getItem('student-password-changed-notice') || '';
    sessionStorage.removeItem('student-password-changed-notice');
    return notice;
  });

  const set = (k) => (e) => {
    setFields(f => ({ ...f, [k]: e.target.value }));
    setFieldErrors(current => ({ ...current, [k]: '' }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    const nextErrors = {};
    if (!fields.password) nextErrors.password = 'Enter your password.';
    if (mode === 'email' && !fields.email.trim()) nextErrors.email = 'Enter your email address.';
    if (mode === 'email' && fields.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim())) nextErrors.email = 'Enter a valid email address.';
    if (mode === 'email' && schoolCodeRequired && !fields.schoolCode.trim()) nextErrors.schoolCode = 'Enter your school code to choose the correct school.';
    if (mode === 'code' && !fields.schoolCode.trim()) nextErrors.schoolCode = 'Enter your school code.';
    if (mode === 'code' && fields.schoolCode && !/^[a-z0-9]{6}$/i.test(fields.schoolCode.trim())) nextErrors.schoolCode = 'School code must be 6 letters or numbers.';
    if (mode === 'code' && !fields.studentId.trim()) nextErrors.studentId = 'Enter your school-assigned student ID.';
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setLoading(true);
    try {
      const body = mode === 'email'
        ? { email: fields.email, password: fields.password, ...(schoolCodeRequired ? { schoolCode: fields.schoolCode } : {}) }
        : { schoolCode: fields.schoolCode.toUpperCase(), studentId: fields.studentId, password: fields.password };

      const { ok, data } = await postJson('/school/auth/student-login', body);
      if (ok && data.token) {
        localStorage.setItem('school-token', data.token);
        localStorage.setItem('school-actor', 'student');
        onSuccess('student', data);
      } else {
        if (data.code === 'MULTIPLE_SCHOOLS') {
          setSchoolCodeRequired(true);
          setFieldErrors({ schoolCode: data.message || 'Enter your school code to choose the correct school.' });
          setError(data.message || 'This account is linked to multiple schools. Enter your school code.');
        } else if (data.field) {
          setFieldErrors({ [data.field]: data.message || 'Please check this field.' });
          if (data.code === 'TOO_MANY_ATTEMPTS') setError(data.message || `Try again in ${data.retryAfterSeconds} seconds.`);
        } else {
          setError(data.message || 'Login failed. Please try again.');
        }
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [mode, fields, schoolCodeRequired, onSuccess]);

  return (
    <form onSubmit={handleSubmit} className={css.formPanel}>
      <h2 className={css.formTitle}>Student Login</h2>
      <p className={css.formSub}>Access your school exams and study plans</p>
      {passwordChangedNotice && <div className={css.noticeMsg}>{passwordChangedNotice}</div>}

      {/* Mode toggle */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {[{ v: 'email', l: 'Email' }, { v: 'code', l: 'School Code' }].map(({ v, l }) => (
          <button
            key={v}
            type="button"
            onClick={() => { setMode(v); setError(''); setFieldErrors({}); setSchoolCodeRequired(false); }}
            style={{
              flex: 1,
              padding: '8px',
              border: `1.5px solid ${mode === v ? '#5fbec5' : '#e0e8ea'}`,
              borderRadius: '10px',
              background: mode === v ? 'rgba(95,190,197,0.08)' : 'transparent',
              color: mode === v ? '#0C1E28' : '#90a4ae',
              fontWeight: mode === v ? 700 : 500,
              cursor: 'pointer',
              fontSize: '0.83rem',
              fontFamily: 'inherit',
              transition: 'all 0.18s',
            }}
          >
            {l}
          </button>
        ))}
      </div>

      {error && <div className={css.errorMsg}>{error}</div>}

      {mode === 'email' ? (
        <div className={css.inputGroup}>
          <label className={css.inputLabel} htmlFor="student-login-email">Email Address</label>
          <input id="student-login-email" className={`${css.inputField} ${fieldErrors.email ? css.inputFieldError : ''}`} type="email" placeholder="you@school.com" value={fields.email} onChange={set('email')} required aria-invalid={Boolean(fieldErrors.email)} />
          {fieldErrors.email && <span className={css.fieldError}>{fieldErrors.email}</span>}
          {schoolCodeRequired && <>
            <label className={css.inputLabel} htmlFor="student-login-school-code">School Code</label>
            <input id="student-login-school-code" className={`${css.inputField} ${fieldErrors.schoolCode ? css.inputFieldError : ''}`} type="text" placeholder="e.g. AB12CD" value={fields.schoolCode} onChange={set('schoolCode')} maxLength={6} required aria-invalid={Boolean(fieldErrors.schoolCode)} />
            {fieldErrors.schoolCode && <span className={css.fieldError}>{fieldErrors.schoolCode}</span>}
          </>}
        </div>
      ) : (
        <>
          <div className={css.inputGroup}>
            <label className={css.inputLabel} htmlFor="student-login-school-code">School Code</label>
            <input id="student-login-school-code" className={`${css.inputField} ${fieldErrors.schoolCode ? css.inputFieldError : ''}`} type="text" placeholder="e.g. AB12CD" value={fields.schoolCode} onChange={set('schoolCode')} maxLength={6} required aria-invalid={Boolean(fieldErrors.schoolCode)} />
            <span className={css.schoolCodeHint}>6-character code from your school</span>
            {fieldErrors.schoolCode && <span className={css.fieldError}>{fieldErrors.schoolCode}</span>}
          </div>
          <div className={css.inputGroup}>
            <label className={css.inputLabel} htmlFor="student-login-student-id">Student ID</label>
            <input id="student-login-student-id" className={`${css.inputField} ${fieldErrors.studentId ? css.inputFieldError : ''}`} type="text" placeholder="School-assigned student ID" value={fields.studentId} onChange={set('studentId')} required aria-invalid={Boolean(fieldErrors.studentId)} />
            {fieldErrors.studentId && <span className={css.fieldError}>{fieldErrors.studentId}</span>}
          </div>
        </>
      )}

      <div className={css.inputGroup}>
        <label className={css.inputLabel} htmlFor="student-login-password">Password</label>
        <SchoolPasswordInput id="student-login-password" className={`${css.inputField} ${fieldErrors.password ? css.inputFieldError : ''}`} placeholder="••••••••" value={fields.password} onChange={set('password')} autoComplete="current-password" required aria-invalid={Boolean(fieldErrors.password)} />
        {fieldErrors.password && <span className={css.fieldError}>{fieldErrors.password}</span>}
      </div>

      <button type="submit" className={css.submitBtn} disabled={loading}>
        {loading
          ? <ThreeCircles height="22" width="22" color="#fff" visible={true} />
          : <><LoginIcon style={{ fontSize: '1.1rem' }} /> Sign In</>}
      </button>

      <div className={css.linkRow}>
        <span>Forgot your password?&nbsp;</span>
        <span className={css.link} onClick={() => onForgotPassword('student')}>Reset it</span>
      </div>

      <div className={css.legalDisclaimerRow}>
        By signing in, you agree to our{' '}
        <span className={css.legalDisclaimerLink} onClick={() => onOpenAgreement && onOpenAgreement('student')}>
          Student Agreement
        </span>{' '}
        &amp;{' '}
        <span className={css.legalDisclaimerLink} onClick={() => onOpenAgreement && onOpenAgreement('privacy')}>
          NDPA Privacy Policy
        </span>.
      </div>
    </form>
  );
}

// ─── Parent Login (email + password, with schoolCode disambiguation) ────────
function ParentForm({ onSuccess, onForgotPassword, onOpenAgreement }) {
  const [fields, setFields] = useState({ email: '', password: '', schoolCode: '' });
  const [needsSchoolCode, setNeedsSchoolCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const set = (k) => (e) => setFields(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { ok, status, data } = await postJson('/school/auth/parent-login', fields);
      if (ok && data.token) {
        onSuccess('parent', data);
      } else if (status === 409) {
        setNeedsSchoolCode(true);
        setError(data.message || 'This email is linked to multiple schools. Please enter your school code.');
      } else {
        setError(data.message || 'Login failed. Please check your credentials.');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fields, onSuccess]);

  return (
    <form onSubmit={handleSubmit} className={css.formPanel}>
      <h2 className={css.formTitle}>Parent Login</h2>
      <p className={css.formSub}>Track your child's fees, attendance, and exam results</p>

      {error && <div className={css.errorMsg}>{error}</div>}

      <div className={css.inputGroup}>
        <label className={css.inputLabel}>Email Address</label>
        <input className={css.inputField} type="email" placeholder="you@example.com" value={fields.email} onChange={set('email')} required />
      </div>
      <div className={css.inputGroup}>
        <label className={css.inputLabel}>Password</label>
        <SchoolPasswordInput className={css.inputField} placeholder="••••••••" value={fields.password} onChange={set('password')} autoComplete="current-password" required />
      </div>
      {needsSchoolCode && (
        <div className={css.inputGroup}>
          <label className={css.inputLabel}>School Code</label>
          <input className={css.inputField} type="text" placeholder="e.g. AB12CD" value={fields.schoolCode} onChange={set('schoolCode')} maxLength={6} required />
        </div>
      )}

      <button type="submit" className={css.submitBtn} disabled={loading}>
        {loading
          ? <ThreeCircles height="22" width="22" color="#fff" visible={true} />
          : <><LoginIcon style={{ fontSize: '1.1rem' }} /> Sign In</>}
      </button>

      <div className={css.linkRow}>
        <span>Forgot your password?&nbsp;</span>
        <span className={css.link} onClick={() => onForgotPassword('parent')}>Reset it</span>
      </div>

      <div className={css.legalDisclaimerRow}>
        By logging in, you agree to our{' '}
        <span className={css.legalDisclaimerLink} onClick={() => onOpenAgreement && onOpenAgreement('parent')}>
          Parent Terms
        </span>{' '}
        &amp;{' '}
        <span className={css.legalDisclaimerLink} onClick={() => onOpenAgreement && onOpenAgreement('fees')}>
          Fee Payment Agreement
        </span>.
      </div>
    </form>
  );
}

function StaffForm({ role, onSuccess, onForgotPassword, onOpenAgreement }) {
  const isAdmin = role === 'admin';
  const [fields, setFields] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const set = (k) => (e) => setFields(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const url = isAdmin ? '/school/auth/admin-login' : '/school/auth/teacher-login';
    try {
      const { ok, data } = await postJson(url, fields);
      if (ok && data.token) {
        localStorage.setItem('school-token', data.token);
        localStorage.setItem('school-actor', role);
        onSuccess(role, data);
      } else {
        setError(data.message || 'Login failed. Please check your credentials.');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fields, isAdmin, role, onSuccess]);

  return (
    <form onSubmit={handleSubmit} className={css.formPanel}>
      <h2 className={css.formTitle}>{isAdmin ? 'Admin Login' : 'Teacher Login'}</h2>
      <p className={css.formSub}>
        {isAdmin
          ? 'Manage your school — staff, students and exams'
          : 'Access your classes, set exams, track results'}
      </p>

      {error && <div className={css.errorMsg}>{error}</div>}

      <div className={css.inputGroup}>
        <label className={css.inputLabel}>Email Address</label>
        <input className={css.inputField} type="email" placeholder="you@school.com" value={fields.email} onChange={set('email')} required />
      </div>
      <div className={css.inputGroup}>
        <label className={css.inputLabel}>Password</label>
        <SchoolPasswordInput className={css.inputField} placeholder="••••••••" value={fields.password} onChange={set('password')} autoComplete="current-password" required />
      </div>

      <button type="submit" className={css.submitBtn} disabled={loading}>
        {loading
          ? <ThreeCircles height="22" width="22" color="#fff" visible={true} />
          : <><LoginIcon style={{ fontSize: '1.1rem' }} /> Sign In</>}
      </button>

      <div className={css.linkRow}>
        <span>Forgot your password?&nbsp;</span>
        <span className={css.link} onClick={() => onForgotPassword(role)}>Reset it</span>
      </div>

      {isAdmin && (
        <div className={css.linkRow} style={{ marginTop: '10px' }}>
          <span>New to Bubble Ai Schools?&nbsp;</span>
          <span
            className={css.link}
            onClick={() => document.getElementById('school-pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            Get started →
          </span>
        </div>
      )}

      <div className={css.legalDisclaimerRow}>
        By signing in, you agree to our{' '}
        <span className={css.legalDisclaimerLink} onClick={() => onOpenAgreement && onOpenAgreement(isAdmin ? 'admin' : 'teacher')}>
          {isAdmin ? 'School Master Agreement & Fee Clawback Terms' : 'Teacher Agreement & Academic Ethics'}
        </span>{' '}
        &amp;{' '}
        <span className={css.legalDisclaimerLink} onClick={() => onOpenAgreement && onOpenAgreement('privacy')}>
          NDPA 2023 Policy
        </span>.
      </div>
    </form>
  );
}

// ─── Student Forgot Password (2-step: send code → verify + set password) ─────
function StudentForgotPasswordForm({ onBack }) {
  const [step, setStep]       = useState(1); // 1 = email, 2 = code + new password
  const [email, setEmail]     = useState('');
  const [code, setCode]       = useState('');
  const [pw, setPw]           = useState('');
  const [pw2, setPw2]         = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);
  const [error, setError]     = useState('');

  const handleSendCode = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await postJson('/auth/pwd-recover', { email });
      if (ok) {
        setStep(2);
      } else {
        setError(data.message || 'Could not send code. Check your email and try again.');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email]);

  const handleSetPassword = useCallback(async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setError('Passwords do not match.'); return; }
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await postJson('/auth/new-password', { email, code, newPassword: pw });
      if (ok) {
        setDone(true);
      } else {
        setError(data.message || 'Invalid or expired code.');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, code, pw, pw2]);

  if (done) {
    return (
      <div className={css.formPanel}>
        <h2 className={css.formTitle} style={{ color: '#3e8f93' }}>Password Updated!</h2>
        <p className={css.formSub}>Your new password has been saved. You can now log in.</p>
        <button className={css.submitBtn} onClick={onBack}>Back to Login</button>
      </div>
    );
  }

  return (
    <div className={css.formPanel}>
      <h2 className={css.formTitle}>Student Password Reset</h2>
      {step === 1 ? (
        <form onSubmit={handleSendCode}>
          <p className={css.formSub}>Enter your student email and we'll send a 8-digit reset code.</p>
          {error && <div className={css.errorMsg}>{error}</div>}
          <div className={css.inputGroup}>
            <label className={css.inputLabel}>Student Email</label>
            <input
              className={css.inputField}
              type="email"
              placeholder="you@school.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>
          <button type="submit" className={css.submitBtn} disabled={loading}>
            {loading ? <ThreeCircles height="22" width="22" color="#fff" visible={true} /> : 'Send Reset Code'}
          </button>
          <div className={css.linkRow} style={{ marginTop: 16 }}>
            <span className={css.link} onClick={onBack}>← Back to login</span>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSetPassword}>
          <p className={css.formSub}>
            A reset code was sent to <strong>{email}</strong>. Enter it below along with your new password.
            <br /><span style={{ fontSize: '0.78rem', color: '#90a4ae' }}>Code expires in 15 minutes.</span>
          </p>
          {error && <div className={css.errorMsg}>{error}</div>}
          <div className={css.inputGroup}>
            <label className={css.inputLabel}>Reset Code</label>
            <input
              className={css.inputField}
              type="text"
              inputMode="numeric"
              placeholder="8-digit code from your email"
              value={code}
              onChange={e => setCode(e.target.value)}
              required
            />
          </div>
          <div className={css.inputGroup}>
            <label className={css.inputLabel}>New Password</label>
            <SchoolPasswordInput
              className={css.inputField}
              placeholder="Min. 8 characters"
              value={pw}
              onChange={e => setPw(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          <div className={css.inputGroup}>
            <label className={css.inputLabel}>Confirm Password</label>
            <SchoolPasswordInput
              className={css.inputField}
              placeholder="Repeat password"
              value={pw2}
              onChange={e => setPw2(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          <button type="submit" className={css.submitBtn} disabled={loading}>
            {loading ? <ThreeCircles height="22" width="22" color="#fff" visible={true} /> : <><LoginIcon style={{ fontSize: '1.1rem' }} /> Set New Password</>}
          </button>
          <div className={css.linkRow} style={{ marginTop: 16 }}>
            <span className={css.link} onClick={() => { setStep(1); setError(''); }}>← Resend code</span>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Forgot Password overlay ─────────────────────────────────────────────────
function ForgotPasswordForm({ initialRole, onBack }) {
  const [role, setRole]       = useState(initialRole === 'student' ? 'admin' : (initialRole || 'admin'));
  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await postJson('/school/auth/forgot-password', { email, role });
      setDone(true);
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, role]);

  return (
    <div className={css.formPanel}>
      <h2 className={css.formTitle}>Reset Password</h2>
      {done ? (
        <>
          <p className={css.formSub} style={{ color: '#3e8f93', fontWeight: 600 }}>
            Check your inbox! If that email is registered, a reset link has been sent.
          </p>
          <div className={css.linkRow} style={{ marginTop: 24 }}>
            <span className={css.link} onClick={onBack}>← Back to login</span>
          </div>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <p className={css.formSub}>Enter your email and we'll send a reset link.</p>
          {error && <div className={css.errorMsg}>{error}</div>}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            {[{ v: 'admin', l: 'I am an Admin' }, { v: 'teacher', l: 'I am a Teacher' }, { v: 'parent', l: 'I am a Parent' }].map(({ v, l }) => (
              <button key={v} type="button" onClick={() => setRole(v)} style={{
                flex: 1, padding: '8px', border: `1.5px solid ${role === v ? '#5fbec5' : '#e0e8ea'}`,
                borderRadius: '10px', background: role === v ? 'rgba(95,190,197,0.08)' : 'transparent',
                color: role === v ? '#0C1E28' : '#90a4ae', fontWeight: role === v ? 700 : 500,
                cursor: 'pointer', fontSize: '0.83rem', fontFamily: 'inherit', transition: 'all 0.18s',
              }}>{l}</button>
            ))}
          </div>
          <div className={css.inputGroup}>
            <label className={css.inputLabel}>Email Address</label>
            <input className={css.inputField} type="email" placeholder="you@school.com" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <button type="submit" className={css.submitBtn} disabled={loading}>
            {loading ? <ThreeCircles height="22" width="22" color="#fff" visible={true} /> : 'Send Reset Link'}
          </button>
          <div className={css.linkRow} style={{ marginTop: 16 }}>
            <span className={css.link} onClick={onBack}>← Back to login</span>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Set Password (invite acceptance + reset) ────────────────────────────────
function SetPasswordForm({ token, type, mode }) {
  const navigate = useNavigate();
  const [pw, setPw]           = useState('');
  const [pw2, setPw2]         = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);
  const [error, setError]     = useState('');

  const heading = mode === 'invite' ? 'Accept Invite' : 'Set New Password';
  const sub     = mode === 'invite'
    ? 'Create a password for your school account'
    : 'Choose a new password for your account';

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setError('Passwords do not match'); return; }
    if (pw.length < 8) { setError('Password must be at least 8 characters'); return; }
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await postJson('/school/auth/reset-password', { token, newPassword: pw, type });
      if (ok) {
        setDone(true);
      } else {
        setError(data.message || 'Failed. The link may have expired.');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [pw, pw2, token, type]);

  if (done) {
    return (
      <div className={css.formPanel}>
        <h2 className={css.formTitle} style={{ color: '#3e8f93' }}>All set!</h2>
        <p className={css.formSub}>Your password has been saved. You can now log in.</p>
        <button className={css.submitBtn} onClick={() => navigate('/school', { replace: true })}>
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={css.formPanel}>
      <h2 className={css.formTitle}>{heading}</h2>
      <p className={css.formSub}>{sub}</p>
      {error && <div className={css.errorMsg}>{error}</div>}
      <div className={css.inputGroup}>
        <label className={css.inputLabel}>New Password</label>
        <SchoolPasswordInput className={css.inputField} placeholder="Min. 8 characters" value={pw} onChange={e => setPw(e.target.value)} autoComplete="new-password" required />
      </div>
      <div className={css.inputGroup}>
        <label className={css.inputLabel}>Confirm Password</label>
        <SchoolPasswordInput className={css.inputField} placeholder="Repeat password" value={pw2} onChange={e => setPw2(e.target.value)} autoComplete="new-password" required />
      </div>
      <button type="submit" className={css.submitBtn} disabled={loading}>
        {loading ? <ThreeCircles height="22" width="22" color="#fff" visible={true} /> : <><LoginIcon style={{ fontSize: '1.1rem' }} /> {mode === 'invite' ? 'Activate Account' : 'Set Password'}</>}
      </button>
    </form>
  );
}

// ─── School Interest Form ─────────────────────────────────────────────────────

const SCHOOL_TYPES    = ['primary','secondary','tertiary','other'];
const STUDENT_RANGES  = ['<100','100–300','300–600','600–1,000','1,000+'];
const TEACHER_RANGES  = ['<10','10–25','25–60','60+'];
const HEARD_OPTIONS   = ['Search Engine','Social Media','Referral / Word of Mouth','Education Event','Government / Ministry Listing','Other'];
const CONTACT_ROLES   = ['Principal','Vice Principal','IT Coordinator','School Administrator','Bursar','Other'];

// Student-count range → numeric bounds for price estimate
const STUDENT_RANGE_NUMS = {
  '<100':       { min: 1,    max: 99   },
  '100–300':   { min: 100,  max: 300  },
  '300–600':   { min: 300,  max: 600  },
  '600–1,000': { min: 600,  max: 1000 },
  '1,000+':     { min: 1000, max: null },
};

const EMPTY_REQUEST   = {
  schoolName: '', schoolType: 'secondary', country: 'Nigeria', state: '', city: '', address: '',
  estimatedStudents: '', estimatedTeachers: '', currentExamSystem: '',
  contactFirstName: '', contactLastName: '', contactEmail: '', contactPhone: '', contactRole: '',
  plan: 'premium', billingCycle: 'term',
  heardAbout: '', additionalInfo: '',
};

function SchoolInterestForm({ open, initialPlan, initialBillingCycle, onClose }) {
  const [fields, setFields] = useState({ ...EMPTY_REQUEST, plan: initialPlan || 'premium', billingCycle: initialBillingCycle || 'term' });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(false);
  // Live plan pricing — single source of truth, fetched from the admin-editable
  // plan config (see utils/schoolPlans.js on the server).
  const [plans, setPlans] = useState(null); // { standard: {...}, premium: {...} }

  useEffect(() => {
    let cancelled = false;
    fetch('/school/auth/plans')
      .then(r => r.json())
      .then(data => { if (!cancelled) setPlans(data.plans || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Keep plan + billing in sync when CTA re-clicked
  useEffect(() => {
    if (open) {
      setFields(f => ({ ...f, plan: initialPlan || 'premium', billingCycle: initialBillingCycle || 'term' }));
      setError('');
      setSuccess(false);
    }
  }, [open, initialPlan, initialBillingCycle]);

  // Dynamic price estimate (per-student × range)
  const priceEstimate = useMemo(() => {
    const { estimatedStudents, plan, billingCycle } = fields;
    if (!estimatedStudents || !plans) return null;
    const range = STUDENT_RANGE_NUMS[estimatedStudents];
    if (!range) return null;
    const cfg = plans[plan] || plans.standard;
    if (!cfg) return null;
    const perStudent = billingCycle === 'year' && plan === 'premium' ? cfg.perYearPrice : cfg.perTermPrice;
    const minTotal = range.min * perStudent;
    const maxTotal = range.max ? range.max * perStudent : null;
    return { perStudent, minTotal, maxTotal, unbounded: !range.max };
  }, [fields, plans]);

  const set = (k) => (e) => setFields(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    // Client-side required check
    if (!fields.schoolName.trim()) return setError('School name is required.');
    if (!fields.state.trim())      return setError('State is required.');
    if (!fields.contactFirstName.trim() || !fields.contactLastName.trim()) return setError('Your full name is required.');
    if (!fields.contactEmail.trim()) return setError('Your email address is required.');

    setLoading(true);
    try {
      const res = await fetch('/school/auth/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(true);
      } else if (res.status === 409) {
        setError(data.error || 'A request from this school or email already exists. Our team will be in touch shortly.');
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Connection error. Please check your internet and try again.');
    } finally {
      setLoading(false);
    }
  }, [fields]);

  if (!open) return null;

  const labelCls  = css.reqLabel;
  const inputCls  = css.reqInput;
  const selectCls = css.reqSelect;

  return (
    <div className={css.reqOverlay} role="dialog" aria-modal="true" aria-label="School Interest Form">
      <div className={`${css.reqModal} BubblePopIn`}>

        {/* ── Header ── */}
        <div className={css.reqHeader}>
          <div>
            <h2 className={css.reqTitle}>Get Started with Bubble Ai Schools</h2>
            <p className={css.reqSubtitle}>Fill in your school details and we'll be in touch to set everything up.</p>
          </div>
          <button className={css.reqClose} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {success ? (
          <div className={css.reqSuccess}>
            <div className={css.reqSuccessIcon}>🎉</div>
            <h3>Request Received!</h3>
            <p>
              Thank you! Our team has received your interest in the&nbsp;
              <strong>{fields.plan === 'premium' ? 'Premium' : 'Standard'} Plan</strong>.
              We'll reach out to <strong>{fields.contactEmail}</strong> within 1–2 business days to get your school onboarded.
            </p>
            <button className={css.reqCloseBtn} onClick={onClose}>Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={css.reqForm}>

            {/* ── Plan selector (always visible, changeable) ── */}
            <div className={css.reqPlanToggle}>
              {['standard','premium'].map(p => (
                <button
                  key={p}
                  type="button"
                  className={`${css.reqPlanBtn} ${fields.plan === p ? css.reqPlanBtnActive : ''} ${p === 'premium' ? css.reqPlanBtnPremium : ''}`}
                  onClick={() => setFields(f => ({ ...f, plan: p }))}
                >
                  {p === 'premium' ? '⭐ Premium' : 'Standard'}
                </button>
              ))}
              {fields.plan === 'premium' && (
                <div className={css.reqBillingToggle}>
                  {[['term','Per Term'],['year', `Per Year${plans?.premium?.yearlyDiscountPercent ? ` (${plans.premium.yearlyDiscountPercent}% off)` : ''}`]].map(([v,l]) => (
                    <button
                      key={v}
                      type="button"
                      className={`${css.reqBillingBtn} ${fields.billingCycle === v ? css.reqBillingBtnActive : ''}`}
                      onClick={() => setFields(f => ({ ...f, billingCycle: v }))}
                    >{l}</button>
                  ))}
                </div>
              )}
            </div>

            {/* ── Section: School Details ── */}
            <div className={css.reqSection}>
              <div className={css.reqSectionTitle}>School Details</div>
              <div className={css.reqGrid2}>
                <div className={css.reqField}>
                  <label className={labelCls}>School Name <span className={css.reqRequired}>*</span></label>
                  <input className={inputCls} value={fields.schoolName} onChange={set('schoolName')} placeholder="e.g. Greenfield Academy" required />
                </div>
                <div className={css.reqField}>
                  <label className={labelCls}>School Type <span className={css.reqRequired}>*</span></label>
                  <select className={selectCls} value={fields.schoolType} onChange={set('schoolType')}>
                    {SCHOOL_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div className={css.reqGrid2}>
                <div className={css.reqField}>
                  <label className={labelCls}>State <span className={css.reqRequired}>*</span></label>
                  <input className={inputCls} value={fields.state} onChange={set('state')} placeholder="e.g. Lagos" required />
                </div>
                <div className={css.reqField}>
                  <label className={labelCls}>City / Town</label>
                  <input className={inputCls} value={fields.city} onChange={set('city')} placeholder="e.g. Ikeja" />
                </div>
              </div>
              <div className={css.reqField}>
                <label className={labelCls}>School Address</label>
                <input className={inputCls} value={fields.address} onChange={set('address')} placeholder="Street address (optional)" />
              </div>
              <div className={css.reqGrid2}>
                <div className={css.reqField}>
                  <label className={labelCls}>Approx. Number of Students</label>
                  <select className={selectCls} value={fields.estimatedStudents} onChange={set('estimatedStudents')}>
                    <option value="">Select range</option>
                    {STUDENT_RANGES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className={css.reqField}>
                  <label className={labelCls}>Approx. Number of Teachers</label>
                  <select className={selectCls} value={fields.estimatedTeachers} onChange={set('estimatedTeachers')}>
                    <option value="">Select range</option>
                    {TEACHER_RANGES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              {/* ── Price Estimate Panel ── */}
              {priceEstimate && (
                <div className={`${css.reqPriceBox} ${fields.plan === 'premium' ? css.reqPriceBoxPremium : ''}`}>
                  <div className={css.reqPriceBoxIcon}>{fields.plan === 'premium' ? '⭐' : '📊'}</div>
                  <div className={css.reqPriceBoxContent}>
                    <div className={css.reqPriceBoxLabel}>
                      Estimated {fields.plan === 'premium' && fields.billingCycle === 'year' ? 'annual' : 'term'} investment
                    </div>
                    <div className={css.reqPriceBoxAmount}>
                      {priceEstimate.unbounded
                        ? `from ${formatNaira(priceEstimate.minTotal)}`
                        : `${formatNaira(priceEstimate.minTotal)} – ${formatNaira(priceEstimate.maxTotal)}`}
                    </div>
                    <div className={css.reqPriceBoxSub}>
                      {formatNaira(priceEstimate.perStudent)} / student
                      {fields.plan === 'premium'
                        ? fields.billingCycle === 'year'
                          ? ` · per year${plans?.premium?.yearlyDiscountPercent ? ` (${plans.premium.yearlyDiscountPercent}% off)` : ''}`
                          : ' · per term'
                        : ' · per term'}
                    </div>
                  </div>
                </div>
              )}

              <div className={css.reqField}>
                <label className={labelCls}>How are you currently managing exams? <span className={css.reqOptional}>(optional)</span></label>
                <input className={inputCls} value={fields.currentExamSystem} onChange={set('currentExamSystem')} placeholder="e.g. Paper-based, Google Forms, another platform..." />
              </div>
            </div>

            {/* ── Section: Your Details ── */}
            <div className={css.reqSection}>
              <div className={css.reqSectionTitle}>Your Details</div>
              <div className={css.reqGrid2}>
                <div className={css.reqField}>
                  <label className={labelCls}>First Name <span className={css.reqRequired}>*</span></label>
                  <input className={inputCls} value={fields.contactFirstName} onChange={set('contactFirstName')} placeholder="First name" required />
                </div>
                <div className={css.reqField}>
                  <label className={labelCls}>Last Name <span className={css.reqRequired}>*</span></label>
                  <input className={inputCls} value={fields.contactLastName} onChange={set('contactLastName')} placeholder="Last name" required />
                </div>
              </div>
              <div className={css.reqGrid2}>
                <div className={css.reqField}>
                  <label className={labelCls}>Your Email <span className={css.reqRequired}>*</span></label>
                  <input className={inputCls} type="email" value={fields.contactEmail} onChange={set('contactEmail')} placeholder="you@yourschool.edu.ng" required />
                </div>
                <div className={css.reqField}>
                  <label className={labelCls}>Phone Number</label>
                  <input className={inputCls} type="tel" value={fields.contactPhone} onChange={set('contactPhone')} placeholder="+234 800 000 0000" />
                </div>
              </div>
              <div className={css.reqField}>
                <label className={labelCls}>Your Role at the School <span className={css.reqRequired}>*</span></label>
                <select className={selectCls} value={fields.contactRole} onChange={set('contactRole')} required>
                  <option value="">Select your role</option>
                  {CONTACT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            {/* ── Section: Anything Else ── */}
            <div className={css.reqSection}>
              <div className={css.reqSectionTitle}>A Little More <span className={css.reqOptionalSection}>(optional)</span></div>
              <div className={css.reqField}>
                <label className={labelCls}>How did you hear about Bubble Ai?</label>
                <select className={selectCls} value={fields.heardAbout} onChange={set('heardAbout')}>
                  <option value="">Select one</option>
                  {HEARD_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className={css.reqField}>
                <label className={labelCls}>Anything else you'd like us to know?</label>
                <textarea
                  className={`${inputCls} ${css.reqTextarea}`}
                  value={fields.additionalInfo}
                  onChange={set('additionalInfo')}
                  placeholder="Special requirements, questions, or anything you'd like to share..."
                  rows={3}
                />
              </div>
            </div>

            {error && <div className={css.reqError}>{error}</div>}

            <button type="submit" className={`${css.reqSubmit} ${fields.plan === 'premium' ? css.reqSubmitPremium : ''}`} disabled={loading}>
              {loading
                ? <ThreeCircles height="20" width="20" color="#0C1E28" visible={true} />
                : `Submit Interest — ${fields.plan === 'premium' ? 'Premium ⭐' : 'Standard'} Plan`}
            </button>

            <p className={css.reqFootnote}>
              By submitting you agree to be contacted by the Bubble Ai Schools team. No payment is required at this stage.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Subscription Plans ──────────────────────────────────────────────────────

function getStandardFeatures(maxTeachers) {
  return [
    { text: 'Price is per student — pay only for who you enrol' },
    { text: `Unlimited students & ${maxTeachers} teachers` },
    { text: '15 internal exams per term' },
    { text: 'Class & subject management' },
    { text: 'Teacher portal & exam builder with image upload' },
    { text: 'Automated grading & instant results' },
    { text: 'Student performance analytics' },
    { text: '1 GB cloud storage for files, diagrams & logos' },
    { text: 'Email & in-app notifications for teachers & admin' },
  ];
}

function getPremiumFeatures(maxTeachers) {
  return [
    { text: 'Price is per student — pay only for who you enrol' },
    { text: `Unlimited students & ${maxTeachers} teachers` },
    { text: 'Unlimited exams per term (internal + external mock)' },
    { text: 'AI-generated question banks' },
    { text: 'Advanced analytics: class, subject & teacher breakdown' },
    { text: 'PDF & CSV report exports with school logo' },
    { text: 'Personalised AI study plans with Audio-Visuals' },
    { text: 'Career path recommendations & guidance' },
    { text: 'Personal AI tutor for every student' },
    { text: 'School Management System' },
    { text: '3 GB cloud storage for file, diagrams & logos (expandable)' },
    { text: 'Email & in-app notifications for students, teachers, admin & parents' },
    { text: 'Plus all Standard Plan features' },
  ];
}

function formatNaira(amount) {
  return '₦' + Math.round(amount).toLocaleString('en-NG');
}

function SubscriptionPlans({ onSelectPlan }) {
  const [billing, setBilling] = useState('term'); // 'term' | 'year'
  // Live plan pricing — single source of truth, fetched from the admin-editable
  // plan config (see utils/schoolPlans.js + controllers/school-plans-admin-controller.js
  // on the server). Main Bubble Ai admin edits these from the origin panel.
  const [plans, setPlans] = useState(null); // { standard: {...}, premium: {...} }

  useEffect(() => {
    let cancelled = false;
    fetch('/school/auth/plans')
      .then(r => r.json())
      .then(data => { if (!cancelled) setPlans(data.plans || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isYear = billing === 'year';
  const periodLabel = isYear ? '/ year' : '/ term';

  if (!plans) {
    return (
      <div className={css.plansSection}>
        <div className={css.plansHeading}>
          <h3>Choose Your School Plan</h3>
          <p>Loading pricing…</p>
        </div>
      </div>
    );
  }

  const standard = plans.standard;
  const premium  = plans.premium;

  const standardShowing = isYear ? standard.perYearPrice : standard.perTermPrice;
  const standardOrig    = isYear ? standard.perYearOriginal : standard.perTermPrice;
  const standardHasDiscount = isYear && standard.yearlyDiscountPercent > 0;

  const premiumShowing = isYear ? premium.perYearPrice : premium.perTermPrice;
  const premiumOrig    = isYear ? premium.perYearOriginal : premium.perTermPrice;
  const premiumHasDiscount = isYear && premium.yearlyDiscountPercent > 0;

  return (
    <div className={css.plansSection}>
      <div className={css.plansHeading}>
        <h3>Choose Your School Plan</h3>
        <p>Simple, transparent pricing. Cancel anytime.</p>
      </div>

      <div className={css.plansGrid}>

        {/* ── Standard Card ── */}
        <div className={`${css.planCard} ${css.planCardStandard}`}>
          <div className={css.planName + ' ' + css.planNameStandard}>Standard</div>

          {/* Billing toggle (shared with Premium via `billing` state) */}
          <div className={css.billingToggle}>
            <button
              type="button"
              className={`${css.billingToggleBtn} ${!isYear ? css.billingToggleBtnActive : ''}`}
              onClick={() => setBilling('term')}
            >
              Per Term
            </button>
            <button
              type="button"
              className={`${css.billingToggleBtn} ${isYear ? css.billingToggleBtnActive : ''}`}
              onClick={() => setBilling('year')}
            >
              Per Year
            </button>
          </div>

          <div className={css.planPrice}>
            <span className={css.planCurrency}>₦</span>
            <span className={css.planAmount}>{formatNaira(standardShowing).replace('₦','')}</span>
            <span className={css.planPeriod}>{periodLabel}</span>
          </div>

          {standardHasDiscount && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
              <span className={css.planAmountStriked}>{formatNaira(standardOrig)}</span>
              <span className={css.planDiscount}>{standard.yearlyDiscountPercent}% off per year</span>
            </div>
          )}

          <p className={css.planDesc}>
            Everything a growing school needs — exams, grading, and analytics in one place.
          </p>

          <ul className={css.featureList}>
            {getStandardFeatures(standard.maxTeachers).map((f) => (
              <li key={f.text} className={css.featureItem}>
                <span className={`${css.featureCheck} ${css.featureCheckStandard}`}>✓</span>
                {f.text}
              </li>
            ))}
          </ul>

          <button
            className={`${css.planCta} ${css.planCtaStandard}`}
            onClick={() => onSelectPlan && onSelectPlan('standard', billing)}
          >
            Get Started with Standard
          </button>
        </div>

        {/* ── Premium Card ── */}
        <div className={`${css.planCard} ${css.planCardPremium}`}>

          {/* Popular badge */}
          <div className={css.popularBadge}>
            <AutoAwesomeIcon style={{ fontSize: '12px' }} />
            Most Popular
          </div>

          <div className={css.planName + ' ' + css.planNamePremium}>Premium</div>

          {/* Billing toggle */}
          <div className={css.billingToggle}>
            <button
              type="button"
              className={`${css.billingToggleBtn} ${!isYear ? css.billingToggleBtnActive : ''}`}
              onClick={() => setBilling('term')}
            >
              Per Term
            </button>
            <button
              type="button"
              className={`${css.billingToggleBtn} ${isYear ? css.billingToggleBtnActive : ''}`}
              onClick={() => setBilling('year')}
            >
              Per Year
            </button>
          </div>

          <div className={css.planPrice}>
            <span className={css.planCurrency}>₦</span>
            <span className={css.planAmount}>{formatNaira(premiumShowing).replace('₦','')}</span>
            <span className={css.planPeriod}>{periodLabel}</span>
          </div>

          {premiumHasDiscount && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
              <span className={css.planAmountStriked}>{formatNaira(premiumOrig)}</span>
              <span className={css.planDiscount}>{premium.yearlyDiscountPercent}% off per year</span>
            </div>
          )}

          <p className={css.planDesc}>
            The full Bubble Ai experience — AI question generation, unlimited scale, and deep insights.
          </p>

          <ul className={css.featureList}>
            {getPremiumFeatures(premium.maxTeachers).map((f) => (
              <li key={f.text} className={css.featureItem}>
                <span className={`${css.featureCheck} ${css.featureCheckPremium}`}>✓</span>
                {f.text}
              </li>
            ))}
          </ul>

          <button
            className={`${css.planCta} ${css.planCtaPremium}`}
            onClick={() => onSelectPlan && onSelectPlan('premium', billing)}
          >
            {isYear ? 'Get Premium — Best Value' : 'Get Premium'}
          </button>
        </div>
      </div>

      <div className={css.plansDivider}>
        Already have an account? <a href="#login" onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Log in above</a> &nbsp;·&nbsp; All prices in Nigerian Naira (₦). Prices apply per school per term/year.
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SchoolLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab]   = useState('admin');
  const [forgotRole, setForgotRole] = useState(null); // non-null = show forgot password form

  // Legal Agreement Modal state
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementRole, setAgreementRole] = useState('all');

  const handleOpenAgreement = useCallback((roleKey) => {
    setAgreementRole(roleKey || activeTab || 'all');
    setAgreementOpen(true);
  }, [activeTab]);

  // Interest form state
  const [reqFormOpen, setReqFormOpen]       = useState(false);
  const [reqFormPlan, setReqFormPlan]       = useState('premium');
  const [reqFormBilling, setReqFormBilling] = useState('term');

  const handlePlanSelect = useCallback((plan, billingCycle) => {
    setReqFormPlan(plan);
    setReqFormBilling(billingCycle || 'term');
    setReqFormOpen(true);
  }, []);

  // Detect invite / reset tokens in URL
  const inviteToken = searchParams.get('invite');
  const resetToken  = searchParams.get('reset');
  const tokenType   = searchParams.get('type') || 'admin'; // 'admin' | 'teacher' | 'parent'

  // Sync active tab with token type
  useEffect(() => {
    if (!inviteToken && !resetToken) return;
    if (tokenType === 'teacher') setActiveTab('teacher');
    else if (tokenType === 'parent') setActiveTab('parent');
    else if (tokenType === 'admin') setActiveTab('admin');
  }, [inviteToken, resetToken, tokenType]);

  // Reopening the installed PWA always lands on this login start_url — if a still-valid
  // session already exists, skip the form and go straight to the dashboard so the app
  // doesn't appear to "log out" on every close/reopen.
  const existingSessionRole = useMemo(() => {
    if (inviteToken || resetToken) return null; // don't hijack invite/reset links
    const token = localStorage.getItem('school-token');
    const actor = localStorage.getItem('school-actor');
    if (!token || !actor || !DASHBOARD_ROUTES[actor]) return null;
    try {
      const decoded = jwtDecode(token);
      return (decoded.expiration && decoded.expiration > Date.now()) ? actor : null;
    } catch {
      return null;
    }
  }, [inviteToken, resetToken]);

  useEffect(() => {
    if (existingSessionRole) {
      navigate(DASHBOARD_ROUTES[existingSessionRole], { replace: true });
    }
  }, [existingSessionRole, navigate]);

  const handleSuccess = useCallback((role, data) => {
    localStorage.setItem('school-token', data.token);
    localStorage.setItem('school-actor', role);
    if (role === 'student') {
      localStorage.removeItem('school-must-change-pwd');
      localStorage.setItem('token', data.token); // reuse main app token key for students
      if (data.schoolId) localStorage.setItem('school-id', data.schoolId);
      else localStorage.removeItem('school-id');
      if (data.mustChangePassword) {
        localStorage.setItem('school-student-must-change-pwd', '1');
      } else {
        localStorage.removeItem('school-student-must-change-pwd');
      }
      navigate(DASHBOARD_ROUTES.student);
    } else if (role === 'admin') {
      localStorage.removeItem('school-student-must-change-pwd');
      // Flag forced password change for first-time logins
      if (data.mustChangePassword) {
        localStorage.setItem('school-must-change-pwd', '1');
      } else {
        localStorage.removeItem('school-must-change-pwd');
      }
      navigate(DASHBOARD_ROUTES.admin);
    } else if (role === 'parent') {
      localStorage.removeItem('school-student-must-change-pwd');
      if (data.mustChangePassword) {
        localStorage.setItem('school-must-change-pwd', '1');
      } else {
        localStorage.removeItem('school-must-change-pwd');
      }
      navigate(DASHBOARD_ROUTES.parent);
    } else {
      localStorage.removeItem('school-student-must-change-pwd');
      navigate(DASHBOARD_ROUTES.teacher);
    }
  }, [navigate]);

  // Render: set-password form when invite/reset token in URL
  const renderRightPanel = () => {
    if (inviteToken) {
      return <SetPasswordForm token={inviteToken} type={tokenType} mode="invite" />;
    }
    if (resetToken) {
      return <SetPasswordForm token={resetToken} type={tokenType} mode="reset" />;
    }
    if (forgotRole === 'student') {
      return <StudentForgotPasswordForm onBack={() => setForgotRole(null)} />;
    }
    if (forgotRole !== null) {
      return <ForgotPasswordForm initialRole={forgotRole} onBack={() => setForgotRole(null)} />;
    }
    return (
      <>
        <div className={css.roleNav}>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`${css.roleBtn} ${activeTab === key ? css.roleBtnActive : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
        {activeTab === 'student' && (
          <StudentForm key="student" onSuccess={handleSuccess} onForgotPassword={setForgotRole} onOpenAgreement={handleOpenAgreement} />
        )}
        {activeTab === 'admin' && (
          <StaffForm key="admin" role="admin" onSuccess={handleSuccess} onForgotPassword={setForgotRole} onOpenAgreement={handleOpenAgreement} />
        )}
        {activeTab === 'teacher' && (
          <StaffForm key="teacher" role="teacher" onSuccess={handleSuccess} onForgotPassword={setForgotRole} onOpenAgreement={handleOpenAgreement} />
        )}
        {activeTab === 'parent' && (
          <ParentForm key="parent" onSuccess={handleSuccess} onForgotPassword={setForgotRole} onOpenAgreement={handleOpenAgreement} />
        )}
      </>
    );
  };

  const handleScrollToLogin = useCallback(() => {
    document.getElementById('school-login-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleGetStartedFromBenefits = useCallback(() => {
    setActiveTab('admin');
    // Scroll down to the admin registration / pricing form
    setTimeout(() => {
      document.getElementById('school-pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, []);

  // Left panel dynamic content based on activeTab
  const leftPanelData = useMemo(() => {
    return ROLE_PANEL_CONTENT[activeTab] || ROLE_PANEL_CONTENT.admin;
  }, [activeTab]);

  // Valid session found — redirecting via effect above; avoid flashing the login form.
  if (existingSessionRole) {
    return <Fetching />;
  }

  return (
    <div className={css.page}>
      <div className={css.blobs}>
        <div className={`${css.blob} ${css.blob1}`} />
        <div className={`${css.blob} ${css.blob2}`} />
        <div className={`${css.blob} ${css.blob3}`} />
      </div>

      {/* Stakeholder Benefits Matrix Showcase (switches dynamically per active tab) - First thing seen on the page */}
      {!inviteToken && !resetToken && !forgotRole && (
        <StakeholderBenefitsShowcase
          role={activeTab}
          onTabChange={setActiveTab}
          onLoginClick={handleScrollToLogin}
          onGetStartedClick={handleGetStartedFromBenefits}
        />
      )}

      {/* Login Section */}
      <div className={css.card} id="school-login-card">
        <div className={css.left} key={activeTab}>
          <div>
            <div className={css.logoRow}>
              <img src="/favicon.ico" alt="Bubble Ai" className={css.logo} />
              <span className={css.logoLabel}>Bubble Ai Schools</span>
            </div>
            <h2 className={css.leftHeading} style={{ marginTop: '28px', whiteSpace: 'pre-line' }}>
              {leftPanelData.heading}
            </h2>
            <p className={css.leftSub}>
              {leftPanelData.sub}
            </p>
            <div className={css.stats}>
              {leftPanelData.benefits.map(({ Icon, text }) => (
                <div className={css.statItem} key={text}>
                  <span className={css.statIcon}><Icon style={{ fontSize: '1rem' }} /></span>
                  {text}
                </div>
              ))}
            </div>
          </div>
          <div className={css.badge}>
            {leftPanelData.testimonial.quote}
            <span className={css.badgeAuthor}>{leftPanelData.testimonial.author}</span>
          </div>
        </div>

        <div className={css.right}>
          {renderRightPanel()}
        </div>
      </div>

      {/* Subscription plans — visible only on Admin Login tab */}
      {activeTab === 'admin' && !inviteToken && !resetToken && !forgotRole && (
        <div id="school-pricing">
          <SubscriptionPlans onSelectPlan={handlePlanSelect} />
        </div>
      )}

      {/* School interest form modal */}
      <SchoolInterestForm
        open={reqFormOpen}
        initialPlan={reqFormPlan}
        initialBillingCycle={reqFormBilling}
        onClose={() => setReqFormOpen(false)}
      />

      {/* Reusable Master User Agreement Modal */}
      <SchoolAgreementModal
        open={agreementOpen}
        initialRole={agreementRole}
        onClose={() => setAgreementOpen(false)}
      />
    </div>
  );
}
